import React, { useCallback, useEffect, useState } from 'react'
import {
  BankAccounts,
  Button,
  Col,
  Container,
  Form,
  Row,
  SectionTitle,
  useToaster
} from 'react-angle-dashboard-components'
import { useHistory } from 'react-router-dom'
import { Paths } from '../../../routes/paths'
import { useQuery } from 'react-query'
import {
  authService,
  paymentsService,
  plaidService as PlaidService,
  groupService
} from '../../../services'
import { IBusinessOnboardingDTO } from '../../BusinessOnboarding/interfaces'

export interface PlaidAccount {
  id: string
  name: string
  mask: string
  type: string
  subtype: string
}

export interface PlaidResponse {
  institution: {
    institution_id: string
    name: string
  }
  accounts: Array<PlaidAccount>
  link_session_id: string
  public_token: string
}

export interface DisplayAccount {
  item_id?: string
  institution_name: string
  is_default: boolean
  account_id: string
  mask: string
  account_type: string
  has_issues: boolean
}

const mapDTOtoBilling = (data: IBusinessOnboardingDTO) => {
  return {
    groupId: data.group_id,
  }
}

export const BillingDetails = () => {
  const [autopay, setAutopay] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<any[]>([])
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [dirty, setDirty] = useState(false)
  const { show: setToastr } = useToaster()
  const history = useHistory()

  const { data } = useQuery(['payment-settings'], () =>
    paymentsService.getSettings()
  )

  const group = useQuery('getBilling', () => 
    groupService.getInfo(mapDTOtoBilling, true),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      onSuccess: (data: any) => {}
    }
  )

  useEffect(() => {
    if (data) {
      setAutopay(data.autopay)
    }
  }, [data])

  const onAutoPayHandler = useCallback(
    async (checked: boolean) => {
      setAutopay(checked)
      await paymentsService.setSettings({
        autopay: checked
      })
      setToastr({
        message: `Autopay has been ${checked ? 'enabled' : 'disabled'}.`,
        icon: 'icon-check',
        type: 'success'
      })
    },
    [setToastr]
  )

  const handleSaveChanges = async () => {
    const defaultAccount: DisplayAccount = paymentMethods.find(
      (a: DisplayAccount) => a.is_default
    )

    if (!defaultAccount) return

    setIsLoadingList(true)
    try {
      await paymentsService.setAccountAsDefault(defaultAccount)

      setToastr({
        message: 'Changes saved.',
        icon: 'icon-correct',
        type: 'success'
      })
      setIsLoadingList(false)
      setDirty(false)
    } catch {
      setToastr({
        message: 'An error has occurred trying to save the information.',
        icon: 'icon-danger',
        type: 'danger'
      })
      setIsLoadingList(false)
    }
  }

  return (
    <Container className="p-3">
      <section>
        <SectionTitle>Billing details</SectionTitle>

        <div>
          {/* <h2>
            $0/<span className="text-muted">month</span>
          </h2> */}

          {/* TODO: Uncomment when we have BE data for this
          <p className="text-muted">
            24 x Angle Gold, 32 x Angle Silver, 5 x Angle Bronze
          </p> */}
        </div>

        <div>
          <Row>
            <Col>
              <b>Autopay</b>
            </Col>
            <Col className="text-right">
              <Form.Check
                type="switch"
                id="autopay"
                checked={autopay}
                onChange={(e) => onAutoPayHandler(e.target.checked)}
              />
            </Col>
          </Row>
        </div>
      </section>

      <section className="border-top mt-4 pt-4">
        <BankAccounts
          setDefaultAccount={() => {
            setDirty(true)
          }}
          isAutoPay={autopay}
          userAccounts={paymentMethods}
          setUserAccounts={setPaymentMethods}
          paymentsService={paymentsService}
          plaidService={PlaidService}
          authService={authService}
          groupService={groupService}
          groupId={group.data?.groupId}
          onAddAch={() => history.push(Paths.addManualAccount)}
          notUpdateOnSelection
        />
      </section>
      {dirty && (
        <Row className="mt-2">
          <Col>
            There are unsaved changes. Click on "save changes" to update your
            preferences.
          </Col>
        </Row>
      )}

      {paymentMethods.length !== 0 && !isLoadingList ? (
        <section className="text-right my-4">
          <Button onClick={handleSaveChanges}>Save changes</Button>
        </section>
      ) : null}
    </Container>
  )
}

export default BillingDetails
